import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

// Configure axios with credentials for authentication
const axiosWithAuth = axios.create({
  withCredentials: true,
});
import {
  X,
  AlertTriangle,
  CheckCircle2,
  Shield,
  Clock,
  Wrench,
  HardHat,
  Target,
  MapPin,
  Phone,
  User,
  Calendar,
  Clipboard,
  FileText,
  Send,
  Link2,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  ArrowLeft,
  Download,
  Check,
  DollarSign,
  AlertOctagon,
  Ban,
  Scale,
  Hammer,
  Package
} from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SubcontractorSOWBuilder = ({
  estimateId,
  estimateData,
  clientInfo,
  companySettings,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [publicToken, setPublicToken] = useState(null);
  
  // Editable header fields
  const [documentId, setDocumentId] = useState("");
  const [subcontractorName, setSubcontractorName] = useState("");
  const [subcontractorCompany, setSubcontractorCompany] = useState("");
  const [subcontractorPhone, setSubcontractorPhone] = useState("");
  const [subcontractorEmail, setSubcontractorEmail] = useState("");
  const [subcontractorLicense, setSubcontractorLicense] = useState("");
  
  // Trade sections with detailed instructions
  const [tradeSections, setTradeSections] = useState([]);
  
  // Payment milestones
  const [paymentMilestones, setPaymentMilestones] = useState([
    { id: 1, name: "Mobilization Complete", description: "Materials on-site, work area prepared", percentage: 15, criteria: "All materials verified, work zone established, photo documentation submitted" },
    { id: 2, name: "Phase 1 Complete", description: "Foundation/structural work complete", percentage: 30, criteria: "Structural inspection passed, GC walkthrough approved, no deviations from SOW" },
    { id: 3, name: "Phase 2 Complete", description: "Primary construction complete", percentage: 35, criteria: "All primary work complete per SOW, quality inspection passed" },
    { id: 4, name: "Final Completion", description: "All work complete, cleanup done", percentage: 20, criteria: "Punch list complete, final inspection passed, site cleaned, client sign-off" }
  ]);
  
  // Critical terms (non-negotiable)
  const [criticalTerms] = useState([
    {
      title: "SCOPE DEVIATION = NO PAYMENT",
      content: "ANY deviation from this Scope of Work without prior WRITTEN authorization from the General Contractor will result in NON-PAYMENT for that work. Unauthorized work will be removed at subcontractor's expense.",
      type: "warning"
    },
    {
      title: "CLIENT CONTACT PROHIBITED",
      content: "Subcontractor shall NOT communicate directly with the property owner/client regarding scope changes, pricing, scheduling, or any project matters. ALL communication goes through the General Contractor ONLY. Violation = immediate removal from project.",
      type: "danger"
    },
    {
      title: "CHANGE ORDERS",
      content: "Any changes requested by the client MUST be communicated to the General Contractor. Subcontractor is NOT authorized to agree to, quote, or perform any additional work without a signed Change Order from the General Contractor.",
      type: "warning"
    },
    {
      title: "MILESTONE PAYMENT REQUIREMENTS",
      content: "Payment for each milestone requires: (1) Work complete per SOW specifications, (2) GC inspection and approval, (3) Photo documentation submitted, (4) No outstanding deficiencies. Incomplete milestones will NOT be paid until all criteria are met.",
      type: "info"
    }
  ]);
  
  // General terms
  const [generalTerms, setGeneralTerms] = useState([
    "Subcontractor is responsible for all labor, tools, and equipment unless specifically noted",
    "Work hours: 7:00 AM - 6:00 PM unless otherwise approved by GC",
    "Maintain clean and safe work site at all times",
    "All work must comply with local building codes and permit requirements",
    "Insurance certificates must be on file before work begins",
    "Subcontractor responsible for own workers' compensation coverage"
  ]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Get groups from estimate data
  const groupsFromProps = estimateData?.groups || estimateData?.allScopes?.base?.groups || [];
  
  // State for groups fetched from backend (fallback when props are stale)
  const [fetchedGroups, setFetchedGroups] = useState([]);
  
  // Use fetched groups if props are empty but we have an estimateId
  const groups = groupsFromProps.length > 0 ? groupsFromProps : fetchedGroups;
  
  // API base URL
  const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
  
  // Fetch estimate data from backend when props are empty (fixes stale state issue)
  useEffect(() => {
    const fetchEstimateData = async () => {
      // Only fetch if we have an estimateId but props are empty
      if (estimateId && groupsFromProps.length === 0) {
        console.log("[SubcontractorSOWBuilder] Props are empty, fetching from backend...");
        try {
          const response = await axiosWithAuth.get(`${API_BASE}/estimates/${estimateId}/frontend-scopes`);
          const scopes = response.data?.scopes;
          if (scopes?.base?.groups && scopes.base.groups.length > 0) {
            console.log("[SubcontractorSOWBuilder] Fetched groups from backend:", scopes.base.groups.length);
            setFetchedGroups(scopes.base.groups);
          }
        } catch (error) {
          console.warn("[SubcontractorSOWBuilder] Could not fetch estimate data:", error);
        }
      }
    };
    
    fetchEstimateData();
  }, [estimateId, groupsFromProps.length, API_BASE]);
  
  // Calculate grand total
  const calculateTotal = useCallback(() => {
    let total = 0;
    groups.forEach(group => {
      (group.items || []).forEach(item => {
        const qty = parseFloat(item.quantity) || 0;
        const unitCost = parseFloat(item.unitCost) || 0;
        const markup = parseFloat(item.markupPercent) || 0;
        const itemTotal = qty * unitCost * (1 + markup / 100);
        total += itemTotal;
      });
    });
    return total;
  }, [groups]);

  const grandTotal = calculateTotal();
  
  // Load or create subcontractor SOW
  useEffect(() => {
    const loadOrCreateSOW = async () => {
      // CRITICAL FIX: Allow rendering with live data even without estimateId
      const hasLiveData = groups && groups.length > 0;
      
      if (!estimateId && !hasLiveData) {
        setLoading(false);
        return;
      }
      
      if (!estimateId && hasLiveData) {
        // No ID but we have live data - use it directly
        console.log("[SubcontractorSOWBuilder] Using live data (no estimateId)");
        setLoading(false);
        return;
      }
      
      try {
        // Try to load existing SOW
        const response = await axiosWithAuth.get(`${API_BASE}/subcontractor-sow/${estimateId}`);
        
        if (response.data && response.data.public_token) {
          setPublicToken(response.data.public_token);
          // Load saved data if present
          if (response.data.subcontractor_name) setSubcontractorName(response.data.subcontractor_name);
          if (response.data.subcontractor_company) setSubcontractorCompany(response.data.subcontractor_company);
          if (response.data.subcontractor_phone) setSubcontractorPhone(response.data.subcontractor_phone);
          if (response.data.subcontractor_email) setSubcontractorEmail(response.data.subcontractor_email);
          if (response.data.subcontractor_license) setSubcontractorLicense(response.data.subcontractor_license);
          if (response.data.trade_sections?.length > 0) setTradeSections(response.data.trade_sections);
          if (response.data.payment_milestones?.length > 0) setPaymentMilestones(response.data.payment_milestones);
          if (response.data.general_terms?.length > 0) setGeneralTerms(response.data.general_terms);
          setLoading(false);
          return;
        }
      } catch (error) {
        // SOW doesn't exist - that's OK, we'll use live data
        console.log("[SubcontractorSOWBuilder] No saved SOW, using live data");
      }
      
      // Only try to create if we have an estimateId
      if (estimateId) {
        try {
          // Create new SOW
          const createResponse = await axiosWithAuth.post(`${API_BASE}/subcontractor-sow`, {
            estimate_id: estimateId,
            project_name: clientInfo?.project_name || estimateData?.job_name || "Project",
            project_address: clientInfo?.address || "",
            company_name: companySettings?.company_name || "",
            total_value: grandTotal
          });
          
          if (createResponse.data?.public_token) {
            setPublicToken(createResponse.data.public_token);
          }
        } catch (error) {
          console.warn("[SubcontractorSOWBuilder] Failed to create SOW, using live data:", error);
        }
      }
      
      setLoading(false);
    };
    
    loadOrCreateSOW();
  }, [estimateId, API_BASE, clientInfo, companySettings, estimateData, grandTotal, groups]);

  // Save SOW data
  const saveSOW = useCallback(async () => {
    if (!estimateId) return;
    
    setSaving(true);
    try {
      await axiosWithAuth.post(`${API_BASE}/subcontractor-sow`, {
        estimate_id: estimateId,
        document_id: documentId,
        subcontractor_name: subcontractorName,
        subcontractor_company: subcontractorCompany,
        subcontractor_phone: subcontractorPhone,
        subcontractor_email: subcontractorEmail,
        subcontractor_license: subcontractorLicense,
        trade_sections: tradeSections,
        payment_milestones: paymentMilestones,
        general_terms: generalTerms,
        critical_terms: criticalTerms,
        total_value: grandTotal,
        project_name: clientInfo?.project_name || estimateData?.job_name,
        project_address: clientInfo?.address || "",
        company_name: companySettings?.company_name || ""
      });
      toast.success("Scope of Work saved");
    } catch (error) {
      console.error("Failed to save SOW:", error);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  }, [estimateId, API_BASE, documentId, subcontractorName, subcontractorCompany, subcontractorPhone, subcontractorEmail, subcontractorLicense, tradeSections, paymentMilestones, generalTerms, criticalTerms, grandTotal, clientInfo, companySettings, estimateData]);

  // Get detailed build instructions based on group name
  const getDetailedInstructions = (groupName, items) => {
    const name = (groupName || "").toUpperCase();
    
    if (name.includes("FOOTER") || name.includes("POST") || name.includes("FOUNDATION")) {
      return {
        materials: [
          "Concrete mix: 4000 PSI minimum, pre-mixed bags or ready-mix",
          "Post brackets: Simpson Strong-Tie ABU series or equivalent",
          "Gravel: 3/4\" clean crushed stone for drainage",
          "Sonotube forms: 12\" diameter minimum for posts"
        ],
        instructions: [
          "1. Layout: Mark all footer locations per plan. Verify measurements from structure. Double-check diagonal measurements for square.",
          "2. Excavation: Dig to minimum 42\" depth (below frost line). Hole diameter minimum 18\" or as required by code.",
          "3. Gravel Base: Place 4\" of compacted gravel at bottom of each hole for drainage.",
          "4. Form Installation: Set Sonotube forms level with top 2\" above grade. Brace securely.",
          "5. Concrete Pour: Fill forms with concrete. Rod/vibrate to remove air pockets. Set post brackets while wet, verify plumb and alignment.",
          "6. Cure Time: Allow minimum 48 hours cure time before loading. Keep moist if temperatures exceed 80°F."
        ],
        qualityChecks: [
          "Footer depth meets local code requirements",
          "All footers level within 1/4\" of each other",
          "Post brackets centered and aligned",
          "No voids or honeycombing in concrete",
          "Proper cure time observed before next phase"
        ]
      };
    }
    
    if (name.includes("FRAMING") || name.includes("JOIST") || name.includes("BEAM") || name.includes("LEDGER")) {
      return {
        materials: [
          "Ledger board: Pressure-treated lumber, size per plan",
          "Ledger flashing: Self-adhering membrane + metal Z-flashing",
          "Ledger fasteners: 1/2\" x 4\" lag screws with washers, staggered pattern",
          "Joists: Pressure-treated lumber, size and spacing per plan",
          "Joist hangers: Simpson Strong-Tie LUS series, galvanized",
          "Hardware: HDG (hot-dip galvanized) nails and screws only"
        ],
        instructions: [
          "1. Ledger Installation: Remove siding 2\" above ledger height. Install self-adhering membrane over sheathing. Mount ledger with 1/2\" lag screws in staggered pattern, max 16\" OC. Install Z-flashing tucked under siding.",
          "2. Beam Installation: Set beams on post brackets. Verify level. Secure with through-bolts or approved connectors.",
          "3. Joist Layout: Mark joist locations on ledger and beam, 16\" OC unless specified otherwise. Account for stair opening if applicable.",
          "4. Joist Installation: Install joist hangers at each location. Set joists in hangers, crown up. Secure with hanger nails - FULL NAIL every hole.",
          "5. Blocking: Install solid blocking at midspan for joists over 10'. Install rim joist at beam end.",
          "6. Inspection Point: Request framing inspection before proceeding to decking."
        ],
        qualityChecks: [
          "Ledger flashing extends behind siding minimum 4\"",
          "All lag screw holes pre-drilled to prevent splitting",
          "Joists crown up, level across span",
          "All joist hanger holes filled with proper nails",
          "Blocking installed and secured properly"
        ]
      };
    }
    
    if (name.includes("DECK") || name.includes("DECKING")) {
      return {
        materials: [
          "Decking boards: [SPECIFY TYPE] - composite/PT/cedar per plan",
          "Deck screws: Stainless steel or coated, length per decking thickness",
          "Hidden fasteners: [If applicable] system and count per manufacturer",
          "Spacers: 1/8\" for PT lumber, per manufacturer for composite"
        ],
        instructions: [
          "1. First Board: Start at house with first board. Use spacer against wall for expansion. Pre-drill ends within 2\" of board end.",
          "2. Fastening Pattern: Two screws per joist, approximately 1\" from each edge. Maintain consistent spacing.",
          "3. Board Spacing: Use 1/8\" spacers between boards for PT lumber. Follow manufacturer specs for composite.",
          "4. Butt Joints: Stagger joints randomly. Both board ends must land on joist. Leave 1/8\" gap between butted ends.",
          "5. Perimeter: Maintain 1/4\" gap at all fixed objects for expansion.",
          "6. Final Row: Rip final board if necessary. Do not force fit."
        ],
        qualityChecks: [
          "Consistent board spacing throughout",
          "All fasteners set flush, not overdriven",
          "No splits at board ends",
          "Joints staggered, not aligned",
          "Surface free of visible defects"
        ]
      };
    }
    
    if (name.includes("RAILING") || name.includes("RAIL") || name.includes("BALUSTER")) {
      return {
        materials: [
          "Posts: 4x4 pressure-treated or per plan",
          "Post mounting hardware: Simpson Strong-Tie or approved equal",
          "Rails: Top and bottom rail per plan specifications",
          "Balusters: [SPECIFY TYPE] at maximum 4\" spacing (3.5\" clear)",
          "Post caps: [SPECIFY TYPE] if applicable"
        ],
        instructions: [
          "1. Post Layout: Mark post locations, maximum 6' OC for residential (verify code). Corner posts and stair posts as shown.",
          "2. Post Installation: Mount posts using approved hardware. Posts must be plumb in both directions. Secure with specified fasteners.",
          "3. Rail Height: Top of rail minimum 36\" above deck surface (42\" for commercial/high decks). Verify local code.",
          "4. Bottom Rail: Install bottom rail with maximum 4\" clearance above deck surface.",
          "5. Baluster Installation: Space balusters to achieve maximum 4\" clear opening (3.5\" OC typical). Secure top and bottom.",
          "6. Top Rail: Install top rail. Check for wobble and secure any loose connections."
        ],
        qualityChecks: [
          "Rail height meets code (36\" residential min)",
          "Baluster spacing max 4\" clear - sphere test",
          "All posts plumb and secure",
          "No wobble in assembled railing",
          "All fasteners concealed where possible"
        ]
      };
    }
    
    if (name.includes("STAIR") || name.includes("STEP") || name.includes("STRINGER")) {
      return {
        materials: [
          "Stringers: Pressure-treated lumber, minimum 2x12 for cut stringers",
          "Stair hangers: Simpson Strong-Tie LSC series or approved equal",
          "Treads: [SPECIFY] - 2x PT, composite, or per plan",
          "Risers: [SPECIFY] - if solid risers required",
          "Hardware: HDG fasteners throughout"
        ],
        instructions: [
          "1. Rise/Run Calculation: Total rise divided by 7\" (typical). Adjust to achieve consistent rise between 7\" and 7-3/4\". Run minimum 10\".",
          "2. Stringer Layout: Use framing square with stair gauges. Mark all cuts. Verify first and last rise account for tread thickness.",
          "3. Cut Stringers: Cut stringers. DO NOT overcut corners. Use handsaw or recip to finish inside corners.",
          "4. Install Stringers: Mount to deck frame using approved hangers. Anchor bottom to landing pad. Check for plumb and level.",
          "5. Treads: Install treads with 1\" overhang (nosing). Two screws per stringer minimum. Maintain consistent spacing between treads.",
          "6. Stair Rail: Install stair rail at consistent height above stair nosing, parallel to stringer angle."
        ],
        qualityChecks: [
          "All rises equal within 3/8\" variance",
          "All runs equal within 3/8\" variance",
          "Stringers secure, no bounce",
          "Tread nosings aligned",
          "Stair rail graspable and secure"
        ]
      };
    }
    
    // Default
    return {
      materials: items.map(item => `${item.itemName || item.name}: Quantity ${item.quantity} ${item.unit || 'EA'}`),
      instructions: [
        "1. Review all plans and specifications before beginning work",
        "2. Verify all materials match specifications and are free of defects",
        "3. Install per manufacturer instructions and industry best practices",
        "4. Document progress with photos at each major stage",
        "5. Request inspection upon completion before proceeding"
      ],
      qualityChecks: [
        "Work matches approved scope and plans",
        "All materials properly installed",
        "No visible defects in finished work",
        "Area clean and safe",
        "Ready for next phase or inspection"
      ]
    };
  };

  // Initialize trade sections from estimate
  useEffect(() => {
    if (groups.length > 0 && tradeSections.length === 0) {
      const sections = groups.map((group, idx) => {
        const details = getDetailedInstructions(group.name, group.items || []);
        return {
          id: idx,
          name: group.name,
          items: (group.items || []).map((item, itemIdx) => ({
            id: itemIdx,
            name: item.itemName || item.name || "Item",
            quantity: item.quantity || 1,
            unit: item.unit || "EA",
            description: item.description || ""
          })),
          materials: details.materials,
          instructions: details.instructions,
          qualityChecks: details.qualityChecks,
          total: (group.items || []).reduce((sum, item) => {
            const qty = parseFloat(item.quantity) || 0;
            const unitCost = parseFloat(item.unitCost) || 0;
            return sum + (qty * unitCost);
          }, 0)
        };
      });
      setTradeSections(sections);
    }
    
    setDocumentId(`SUB-SOW-${(estimateId || "00001").toUpperCase().slice(-8)}`);
  }, [groups, estimateId]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: prev[sectionId] === false ? true : false
    }));
  };

  // Edit handlers
  const handleEditMaterial = (sectionIdx, materialIdx, value) => {
    setTradeSections(prev => prev.map((s, i) => {
      if (i === sectionIdx) {
        const newMaterials = [...s.materials];
        newMaterials[materialIdx] = value;
        return { ...s, materials: newMaterials };
      }
      return s;
    }));
  };

  const handleAddMaterial = (sectionIdx) => {
    setTradeSections(prev => prev.map((s, i) => {
      if (i === sectionIdx) {
        return { ...s, materials: [...s.materials, "New material specification"] };
      }
      return s;
    }));
  };

  const handleDeleteMaterial = (sectionIdx, materialIdx) => {
    setTradeSections(prev => prev.map((s, i) => {
      if (i === sectionIdx) {
        return { ...s, materials: s.materials.filter((_, idx) => idx !== materialIdx) };
      }
      return s;
    }));
  };

  const handleEditInstruction = (sectionIdx, instructionIdx, value) => {
    setTradeSections(prev => prev.map((s, i) => {
      if (i === sectionIdx) {
        const newInstructions = [...s.instructions];
        newInstructions[instructionIdx] = value;
        return { ...s, instructions: newInstructions };
      }
      return s;
    }));
  };

  const handleAddInstruction = (sectionIdx) => {
    setTradeSections(prev => prev.map((s, i) => {
      if (i === sectionIdx) {
        return { ...s, instructions: [...s.instructions, `${s.instructions.length + 1}. New instruction step`] };
      }
      return s;
    }));
  };

  const handleDeleteInstruction = (sectionIdx, instructionIdx) => {
    setTradeSections(prev => prev.map((s, i) => {
      if (i === sectionIdx) {
        return { ...s, instructions: s.instructions.filter((_, idx) => idx !== instructionIdx) };
      }
      return s;
    }));
  };

  const handleEditQualityCheck = (sectionIdx, checkIdx, value) => {
    setTradeSections(prev => prev.map((s, i) => {
      if (i === sectionIdx) {
        const newChecks = [...s.qualityChecks];
        newChecks[checkIdx] = value;
        return { ...s, qualityChecks: newChecks };
      }
      return s;
    }));
  };

  const handleAddQualityCheck = (sectionIdx) => {
    setTradeSections(prev => prev.map((s, i) => {
      if (i === sectionIdx) {
        return { ...s, qualityChecks: [...s.qualityChecks, "New quality check item"] };
      }
      return s;
    }));
  };

  const handleDeleteQualityCheck = (sectionIdx, checkIdx) => {
    setTradeSections(prev => prev.map((s, i) => {
      if (i === sectionIdx) {
        return { ...s, qualityChecks: s.qualityChecks.filter((_, idx) => idx !== checkIdx) };
      }
      return s;
    }));
  };

  const handleEditMilestone = (idx, field, value) => {
    setPaymentMilestones(prev => prev.map((m, i) => 
      i === idx ? { ...m, [field]: field === 'percentage' ? parseFloat(value) || 0 : value } : m
    ));
  };

  const handleAddMilestone = () => {
    setPaymentMilestones(prev => [...prev, {
      id: Date.now(),
      name: "New Milestone",
      description: "Description",
      percentage: 0,
      criteria: "Completion criteria"
    }]);
  };

  const handleDeleteMilestone = (idx) => {
    setPaymentMilestones(prev => prev.filter((_, i) => i !== idx));
  };

  const handleEditTerm = (idx, value) => {
    setGeneralTerms(prev => prev.map((t, i) => i === idx ? value : t));
  };

  const handleAddTerm = () => {
    setGeneralTerms(prev => [...prev, "New term"]);
  };

  const handleDeleteTerm = (idx) => {
    setGeneralTerms(prev => prev.filter((_, i) => i !== idx));
  };

  const totalPercentage = paymentMilestones.reduce((sum, m) => sum + m.percentage, 0);

  // Check if we have data to work with (either estimateId OR groups with data)
  const hasEstimateData = estimateId || (groups && groups.length > 0);
  
  // EMPTY STATE: No estimate data available
  if (!hasEstimateData) {
    return (
      <div className="min-h-screen pt-14" style={{ backgroundColor: "#020617" }}>
        {/* Header Bar - Fixed at top */}
        <div 
          className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between"
          style={{ 
            backgroundColor: "#0f172a",
            borderBottom: "2px solid rgba(245, 158, 11, 0.5)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)"
          }}
        >
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-all font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>← Exit</span>
          </button>
          
          <div className="flex items-center gap-3">
            <span className="text-amber-400 font-bold">SUBCONTRACTOR SCOPE OF WORK</span>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center justify-center" style={{ minHeight: "calc(100vh - 60px)" }}>
          <div className="text-center p-8 rounded-lg" style={{ backgroundColor: "#0f172a", border: "2px dashed #334155", maxWidth: "500px" }}>
            <h2 className="text-xl font-bold text-white mb-4">No Estimate Data</h2>
            <p className="text-slate-400 mb-6">Create an estimate first using the ESTIMATING COMMAND, then return here to build your Subcontractor SOW.</p>
            <button
              onClick={onClose}
              className="px-6 py-3 rounded font-bold text-white transition-all hover:scale-105"
              style={{ backgroundColor: "#f59e0b" }}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen overflow-y-auto pb-32 pt-14"
      style={{ 
        backgroundColor: "#020617",
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* Fixed Header Bar - Always visible at top */}
      <div 
        className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between"
        style={{ 
          backgroundColor: "#0f172a",
          borderBottom: "2px solid rgba(245, 158, 11, 0.5)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)"
        }}
      >
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-all font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>← Exit</span>
        </button>
        
        <div className="flex items-center gap-3">
          <span className="text-amber-400 font-bold">SUBCONTRACTOR SCOPE OF WORK</span>
          <span className="text-slate-500">|</span>
          <span className="text-slate-400">{clientInfo?.project_name || "Project"}</span>
        </div>
        
        <button
          onClick={onClose}
          className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Document Container */}
      <div 
        className="mx-auto mt-8"
        style={{ 
          maxWidth: "900px",
          backgroundColor: "#0f172a",
          borderRadius: "8px",
          border: "1px solid rgba(245, 158, 11, 0.3)",
          boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════════════════
            HEADER
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div 
          className="px-10 pt-8 pb-6"
          style={{
            background: "linear-gradient(180deg, rgba(245, 158, 11, 0.15) 0%, transparent 100%)",
            borderBottom: "1px solid rgba(245, 158, 11, 0.3)",
            borderRadius: "8px 8px 0 0"
          }}
        >
          {/* GC Company Info */}
          <div className="mb-6">
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">GENERAL CONTRACTOR</p>
            <h1 className="text-2xl font-bold tracking-wide" style={{ color: "#f59e0b", fontFamily: "'Playfair Display', serif" }}>
              {companySettings?.company_name || "Alpha Construction Pros"}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {companySettings?.address || "123 Builder Lane, Construction City, ST 12345"}
            </p>
          </div>
          
          {/* Document Metadata */}
          <div className="flex items-center gap-8 flex-wrap">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">DOCUMENT ID</p>
              <input
                type="text"
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                className="text-white font-bold text-sm bg-transparent border-b border-transparent hover:border-amber-500 focus:border-amber-500 outline-none"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">DATE</p>
              <p className="text-white font-bold text-sm">{formatDate()}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">CONTRACT VALUE</p>
              <p className="text-amber-400 font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {fmt(grandTotal)}
              </p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            TITLE & SUBCONTRACTOR INFO
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Hammer className="h-5 w-5 text-amber-400" />
            <p className="text-xs uppercase tracking-[4px] text-amber-400">SUBCONTRACTOR SCOPE OF WORK</p>
          </div>
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif", lineHeight: 1.2 }}>
            {clientInfo?.project_name || estimateData?.job_name || "Project"}
          </h2>
          
          {/* Info Cards */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            {/* Subcontractor Info - Editable */}
            <div className="p-5 rounded-lg" style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}>
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">SUBCONTRACTOR</p>
              <div className="space-y-2">
                <input
                  type="text"
                  value={subcontractorCompany}
                  onChange={(e) => setSubcontractorCompany(e.target.value)}
                  placeholder="Company Name"
                  className="w-full text-white font-medium bg-transparent border-b border-slate-700 hover:border-amber-500 focus:border-amber-500 outline-none"
                />
                <input
                  type="text"
                  value={subcontractorName}
                  onChange={(e) => setSubcontractorName(e.target.value)}
                  placeholder="Contact Name"
                  className="w-full text-slate-400 bg-transparent border-b border-slate-700 hover:border-amber-500 focus:border-amber-500 outline-none"
                />
                <input
                  type="text"
                  value={subcontractorPhone}
                  onChange={(e) => setSubcontractorPhone(e.target.value)}
                  placeholder="Phone"
                  className="w-full text-slate-400 bg-transparent border-b border-slate-700 hover:border-amber-500 focus:border-amber-500 outline-none"
                />
                <input
                  type="text"
                  value={subcontractorLicense}
                  onChange={(e) => setSubcontractorLicense(e.target.value)}
                  placeholder="License #"
                  className="w-full text-slate-400 bg-transparent border-b border-slate-700 hover:border-amber-500 focus:border-amber-500 outline-none"
                />
              </div>
            </div>
            
            {/* Job Site Info */}
            <div className="p-5 rounded-lg" style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}>
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">JOB SITE</p>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-slate-500 mt-0.5" />
                  <span className="text-white font-medium">{clientInfo?.address || "Address TBD"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">Work Hours: 7:00 AM - 6:00 PM</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            CRITICAL TERMS - NON-NEGOTIABLE
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <AlertOctagon className="h-4 w-4 text-red-400" />
            <p className="text-xs text-red-400 uppercase tracking-widest font-bold">⚠ CRITICAL TERMS - READ CAREFULLY ⚠</p>
          </div>
          
          <div className="space-y-4">
            {criticalTerms.map((term, idx) => (
              <div 
                key={idx}
                className="p-5 rounded-lg"
                style={{ 
                  backgroundColor: term.type === 'danger' ? "rgba(239, 68, 68, 0.15)" : 
                                  term.type === 'warning' ? "rgba(245, 158, 11, 0.15)" : "rgba(59, 130, 246, 0.15)",
                  border: `2px solid ${term.type === 'danger' ? "rgba(239, 68, 68, 0.5)" : 
                          term.type === 'warning' ? "rgba(245, 158, 11, 0.5)" : "rgba(59, 130, 246, 0.5)"}`
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  {term.type === 'danger' ? <Ban className="h-5 w-5 text-red-400" /> : 
                   term.type === 'warning' ? <AlertTriangle className="h-5 w-5 text-amber-400" /> :
                   <Scale className="h-5 w-5 text-blue-400" />}
                  <h4 className={`font-bold ${term.type === 'danger' ? 'text-red-400' : term.type === 'warning' ? 'text-amber-400' : 'text-blue-400'}`}>
                    {term.title}
                  </h4>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">{term.content}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            DETAILED SCOPE BY TRADE
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <Wrench className="h-4 w-4 text-amber-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest">DETAILED SCOPE OF WORK BY TRADE</p>
          </div>
          
          <div className="space-y-4">
            {tradeSections.map((section, sectionIdx) => {
              const isExpanded = expandedSections[`trade-${sectionIdx}`] !== false;
              
              return (
                <div 
                  key={section.id}
                  className="rounded-lg overflow-hidden"
                  style={{ border: "1px solid rgba(245, 158, 11, 0.3)" }}
                >
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(`trade-${sectionIdx}`)}
                    className="w-full px-5 py-4 flex items-center justify-between"
                    style={{ backgroundColor: "rgba(245, 158, 11, 0.15)" }}
                  >
                    <div className="flex items-center gap-3">
                      <HardHat className="h-5 w-5 text-amber-400" />
                      <span className="text-white font-bold text-lg">{section.name}</span>
                      <span className="text-slate-500 text-sm">({section.items.length} items)</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-amber-400 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {fmt(section.total)}
                      </span>
                      {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div style={{ backgroundColor: "rgba(15, 23, 42, 0.5)" }}>
                      {/* Materials Required */}
                      <div className="p-5 border-b border-slate-700/50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-blue-400" />
                            <p className="text-xs text-blue-400 uppercase tracking-wider font-bold">MATERIALS REQUIRED</p>
                          </div>
                          <button onClick={() => handleAddMaterial(sectionIdx)} className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm">
                            <Plus className="h-4 w-4" /> Add
                          </button>
                        </div>
                        <ul className="space-y-2">
                          {section.materials.map((material, materialIdx) => (
                            <li key={materialIdx} className="flex items-start gap-3 group">
                              <span className="text-blue-400 mt-1">•</span>
                              <input
                                type="text"
                                value={material}
                                onChange={(e) => handleEditMaterial(sectionIdx, materialIdx, e.target.value)}
                                className="flex-1 text-slate-300 bg-transparent border-b border-transparent hover:border-blue-500 focus:border-blue-500 outline-none"
                              />
                              <button onClick={() => handleDeleteMaterial(sectionIdx, materialIdx)} className="opacity-0 group-hover:opacity-100 text-red-400">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Installation Instructions */}
                      <div className="p-5 border-b border-slate-700/50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Clipboard className="h-4 w-4 text-amber-400" />
                            <p className="text-xs text-amber-400 uppercase tracking-wider font-bold">INSTALLATION INSTRUCTIONS</p>
                          </div>
                          <button onClick={() => handleAddInstruction(sectionIdx)} className="flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm">
                            <Plus className="h-4 w-4" /> Add Step
                          </button>
                        </div>
                        <ol className="space-y-3">
                          {section.instructions.map((instruction, instructionIdx) => (
                            <li key={instructionIdx} className="flex items-start gap-3 group">
                              <textarea
                                value={instruction}
                                onChange={(e) => handleEditInstruction(sectionIdx, instructionIdx, e.target.value)}
                                className="flex-1 text-slate-300 bg-slate-800/50 border border-slate-700 rounded p-2 outline-none focus:border-amber-500 resize-none"
                                rows={2}
                              />
                              <button onClick={() => handleDeleteInstruction(sectionIdx, instructionIdx)} className="opacity-0 group-hover:opacity-100 text-red-400 mt-2">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ol>
                      </div>
                      
                      {/* Quality Checks */}
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                            <p className="text-xs text-emerald-400 uppercase tracking-wider font-bold">QUALITY VERIFICATION (MUST PASS)</p>
                          </div>
                          <button onClick={() => handleAddQualityCheck(sectionIdx)} className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-sm">
                            <Plus className="h-4 w-4" /> Add
                          </button>
                        </div>
                        <ul className="space-y-2">
                          {section.qualityChecks.map((check, checkIdx) => (
                            <li key={checkIdx} className="flex items-start gap-3 group">
                              <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />
                              <input
                                type="text"
                                value={check}
                                onChange={(e) => handleEditQualityCheck(sectionIdx, checkIdx, e.target.value)}
                                className="flex-1 text-slate-300 bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none"
                              />
                              <button onClick={() => handleDeleteQualityCheck(sectionIdx, checkIdx)} className="opacity-0 group-hover:opacity-100 text-red-400">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            PAYMENT MILESTONES
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">PAYMENT MILESTONES</p>
            </div>
            <div className="flex items-center gap-4">
              <span className={`text-sm font-bold ${totalPercentage === 100 ? 'text-emerald-400' : 'text-red-400'}`}>
                Total: {totalPercentage}%
              </span>
              <button onClick={handleAddMilestone} className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-sm">
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
          </div>
          
          <div className="space-y-3">
            {paymentMilestones.map((milestone, idx) => (
              <div key={milestone.id} className="p-4 rounded-lg group" style={{ backgroundColor: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.3)" }}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={milestone.name}
                      onChange={(e) => handleEditMilestone(idx, 'name', e.target.value)}
                      className="text-white font-bold bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none w-full"
                    />
                    <input
                      type="text"
                      value={milestone.description}
                      onChange={(e) => handleEditMilestone(idx, 'description', e.target.value)}
                      className="text-slate-400 text-sm bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none w-full mt-1"
                    />
                    <div className="mt-2">
                      <p className="text-xs text-emerald-400 uppercase mb-1">Payment Criteria:</p>
                      <input
                        type="text"
                        value={milestone.criteria}
                        onChange={(e) => handleEditMilestone(idx, 'criteria', e.target.value)}
                        className="text-slate-300 text-sm bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none w-full"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 ml-4">
                    <div className="text-right">
                      <p className="text-emerald-400 font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {fmt(grandTotal * (milestone.percentage / 100))}
                      </p>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={milestone.percentage}
                          onChange={(e) => handleEditMilestone(idx, 'percentage', e.target.value)}
                          className="w-12 text-slate-400 text-sm bg-transparent border-b border-transparent hover:border-emerald-500 focus:border-emerald-500 outline-none text-right"
                        />
                        <span className="text-slate-400 text-sm">%</span>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteMilestone(idx)} className="opacity-0 group-hover:opacity-100 text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            GENERAL TERMS
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-slate-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">GENERAL TERMS</p>
            </div>
            <button onClick={handleAddTerm} className="flex items-center gap-1 text-slate-400 hover:text-slate-300 text-sm">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
          
          <div className="p-5 rounded-lg" style={{ backgroundColor: "rgba(30, 41, 59, 0.3)", border: "1px solid rgba(100, 116, 139, 0.2)" }}>
            <ul className="space-y-2">
              {generalTerms.map((term, idx) => (
                <li key={idx} className="flex items-start gap-3 group">
                  <span className="text-slate-500 mt-0.5">•</span>
                  <input
                    type="text"
                    value={term}
                    onChange={(e) => handleEditTerm(idx, e.target.value)}
                    className="flex-1 text-slate-300 bg-transparent border-b border-transparent hover:border-slate-500 focus:border-slate-500 outline-none"
                  />
                  <button onClick={() => handleDeleteTerm(idx)} className="opacity-0 group-hover:opacity-100 text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SIGNATURES
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 pb-10">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-4 w-4 text-slate-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest">AUTHORIZATION</p>
          </div>
          
          <div className="p-6 rounded-lg" style={{ backgroundColor: "rgba(30, 41, 59, 0.3)", border: "1px solid rgba(100, 116, 139, 0.2)" }}>
            <p className="text-slate-400 text-sm mb-6">
              By signing below, Subcontractor acknowledges they have read, understand, and agree to ALL terms, conditions, and scope specifications in this document. Subcontractor confirms they will NOT deviate from this scope without written authorization from the General Contractor.
            </p>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Subcontractor Signature</p>
                <div className="h-16 rounded mb-2 flex items-center justify-center" style={{ backgroundColor: "rgba(15, 23, 42, 0.5)", border: "1px dashed rgba(100, 116, 139, 0.3)" }}>
                  <span className="text-slate-600 text-sm">Sign Here</span>
                </div>
                <p className="text-slate-400 text-sm">Print Name: _______________________</p>
                <p className="text-slate-400 text-sm mt-1">Date: _______________________</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">General Contractor</p>
                <div className="h-16 rounded mb-2 flex items-center justify-center" style={{ backgroundColor: "rgba(15, 23, 42, 0.5)", border: "1px dashed rgba(100, 116, 139, 0.3)" }}>
                  <span className="text-slate-600 text-sm">Sign Here</span>
                </div>
                <p className="text-slate-400 text-sm">Print Name: _______________________</p>
                <p className="text-slate-400 text-sm mt-1">Date: _______________________</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-10 py-6 text-center" style={{ borderTop: "1px solid rgba(100, 116, 139, 0.2)", backgroundColor: "rgba(15, 23, 42, 0.5)" }}>
          <p className="text-slate-500 text-xs">{companySettings?.company_name || "Alpha Construction Pros"} • General Contractor</p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          FIXED BOTTOM ACTION BAR
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="fixed bottom-0 left-0 right-0 z-50 px-6 py-4" style={{ backgroundColor: "rgba(15, 23, 42, 0.95)", borderTop: "1px solid rgba(245, 158, 11, 0.3)", backdropFilter: "blur(10px)" }}>
        <div className="max-w-[900px] mx-auto flex items-center justify-between">
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-amber-400 hover:text-white hover:bg-slate-800 transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">← Back</span>
          </button>
          
          <button onClick={() => setSendModalOpen(true)} className="flex items-center gap-2 px-8 py-3 rounded-lg font-bold text-white transition-all hover:scale-105" style={{ backgroundColor: "#f59e0b" }}>
            <Send className="h-5 w-5" />
            Send to Subcontractor
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          SEND MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {sendModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }} onClick={() => setSendModalOpen(false)}>
          <div className="w-full max-w-md p-6 rounded-lg" style={{ backgroundColor: "#1e293b", border: "1px solid rgba(245, 158, 11, 0.3)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-bold text-lg">Send to Subcontractor</h3>
              <button onClick={() => setSendModalOpen(false)} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            
            <div className="space-y-3">
              <button className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700" style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.3)" }}>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-amber-400" />
                  <div><p className="text-white font-medium">Send via Email</p><p className="text-slate-500 text-sm">Email SOW to subcontractor</p></div>
                </div>
              </button>
              
              <button className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700" style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.3)" }}>
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-5 w-5 text-blue-400" />
                  <div><p className="text-white font-medium">Send via SMS</p><p className="text-slate-500 text-sm">Text link to subcontractor</p></div>
                </div>
              </button>
              
              <button
                onClick={async () => {
                  if (!estimateId) {
                    toast.error("Please select an estimate first to generate a shareable link");
                    return;
                  }
                  if (!publicToken) {
                    toast.error("Please wait for document to be created");
                    return;
                  }
                  await saveSOW();
                  const link = `${window.location.origin}/sow-subcontractor/${publicToken}`;
                  try { await navigator.clipboard.writeText(link); setLinkCopied(true); toast.success("Link copied!"); setTimeout(() => setLinkCopied(false), 3000); } catch (error) { toast.error("Failed to copy"); }
                }}
                disabled={!estimateId || loading}
                className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700"
                style={{ backgroundColor: linkCopied ? "rgba(16, 185, 129, 0.15)" : "rgba(30, 41, 59, 0.5)", border: linkCopied ? "1px solid rgba(16, 185, 129, 0.5)" : "1px solid rgba(100, 116, 139, 0.3)", opacity: !estimateId || loading ? 0.6 : 1 }}>
                <div className="flex items-center gap-3">
                  {linkCopied ? <Check className="h-5 w-5 text-emerald-400" /> : <Link2 className="h-5 w-5 text-emerald-400" />}
                  <div><p className="text-white font-medium">{!estimateId ? "Select Estimate First" : loading ? "Creating..." : !publicToken ? "Preparing..." : linkCopied ? "Link Copied!" : "Copy Link"}</p><p className="text-slate-500 text-sm">{!estimateId ? "An estimate must be selected" : "Share direct link"}</p></div>
                </div>
              </button>
              
              <button className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between hover:bg-slate-700" style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.3)" }}>
                <div className="flex items-center gap-3">
                  <Download className="h-5 w-5 text-purple-400" />
                  <div><p className="text-white font-medium">Create PDF</p><p className="text-slate-500 text-sm">Download printable version</p></div>
                </div>
              </button>
            </div>
            
            <button onClick={() => setSendModalOpen(false)} className="w-full mt-6 py-3 rounded-lg text-slate-400 hover:text-white transition-colors" style={{ backgroundColor: "rgba(30, 41, 59, 0.5)" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcontractorSOWBuilder;
